
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { contracts } from '@/core/realisations/contracts.realisation'

// types
import { ContractRegister, ContractT, DocumentTypeT } from '@/core/types/contracts.types'

// validation
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

export default defineComponent({
    components: {
        modal: Modal,
        'the-select': TheSelect,
        'the-input': TheInput,
        'file-reader': FileReader,
    },
    props: {
        contractId: {
            type: Number,
            default: null,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        contract: {
            type: Object as PropType<ContractRegister>,
            default: null,
        },
        isRegister: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['cancel', 'submit'],
    setup(props, { emit }) {
        const state = reactive({
            contract_id: null as number | null,
            type: null as number | null,
            date: null as string | null,
            expirationDate: null as string | null,
            number: null as string | null,
            files: [] as File[],
        })

        const AttachDocumentValidation = {
            contract_id: { required: requiredIf(() => props.isRegister) },
            type: { required },
            date: { required },
            expirationDate: { required: requiredIf(() => isTariff.value) },
            number: { required },
            files: { required },
        }

        const state_form$ = useVuelidate(AttachDocumentValidation, state)

        const data_state = reactive({
            types: new DataState<DocumentTypeT>({}),
            contracts: new DataState<ContractT>({}),
        })

        const loadContracts = async () => {
            data_state.contracts.pagination.current_page = 1

            const params = {
                page: 1,
                limit: 9999,
                company_id: props.contract.companyId,
            }

            try {
                data_state.contracts.is_loading = true

                const response = await contracts.getList(params)

                data_state.contracts.list = response.data
                data_state.contracts.pagination.total_pages = response.meta.last_page
                data_state.contracts.pagination.total_items = response.meta.total

                data_state.contracts.is_failed = false
                data_state.contracts.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.contracts.failure.catchError(error)

                if (!data_state.contracts.failure.error_state.is_cancel) {
                    data_state.contracts.is_failed = true
                }
            } finally {
                if (!data_state.contracts.failure.error_state.is_cancel) {
                    data_state.contracts.is_loading = false
                }
            }
        }

        const loadTypes = async (page: number) => {
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page, isMain: 0 })
                data_state.types.list = response.data
                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadMoreTypes = async (page: number) => {
            if (page > data_state.types.pagination.total_pages) return
            data_state.types.pagination.current_page = page

            try {
                data_state.types.is_loading = true

                const response = await contracts.getTypes({ page, isMain: 0 })
                data_state.types.list = data_state.types.list.concat(response.data)

                data_state.types.pagination.total_pages = response.meta.last_page

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitSubmit = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (
                !state.contract_id ||
                !state.type ||
                !state.date ||
                !state.number ||
                !state.files.length
            )
                return

            const form_data = new FormData()
            form_data.append('typeId', state.type.toString())
            form_data.append('date', state.date.toString())
            form_data.append('number', state.number)
            form_data.append('file', state.files[0])

            if (state.expirationDate) {
                form_data.append('expirationDate', state.expirationDate.toString())
            }
            if (props.isRegister) {
                form_data.append('registerId', props.contract.id.toString())
                form_data.append('contractId', state.contract_id.toString())
            } else {
                form_data.append('contractId', state.contract_id.toString())
            }

            emit('submit', form_data)
        }

        const isTariff = computed(() => state.type === 11)

        const setInfo = () => {
            if (!props.contract || !props.isRegister) return

            loadContracts()

            state.number = props.contract.number
            if (props.contract.type) state.type = props.contract.type.id
        }

        onBeforeMount(() => {
            loadTypes(1)
            state.contract_id = props.contractId
            setInfo()
        })

        return {
            state,
            state_form$,
            data_state,
            emitCancel,
            emitSubmit,
            loadTypes,
            loadMoreTypes,
            loadContracts,
            //
            isTariff,
        }
    },
})
